import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE_URL}/api`;

const TOKEN_KEY = 'thehuman_access_token';
const REFRESH_KEY = 'thehuman_refresh_token';
const USER_KEY = 'thehuman_user_id';

function getTokens() {
  return { access: localStorage.getItem(TOKEN_KEY), refresh: localStorage.getItem(REFRESH_KEY), userId: localStorage.getItem(USER_KEY) };
}
function setTokens(pair) {
  localStorage.setItem(TOKEN_KEY, pair.access_token);
  localStorage.setItem(REFRESH_KEY, pair.refresh_token);
  localStorage.setItem(USER_KEY, pair.user_id);
}
function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const { access } = getTokens();
  if (access) config.headers.Authorization = `Bearer ${access}`;
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  failedQueue = [];
};

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && !orig._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => { orig.headers.Authorization = `Bearer ${token}`; return client(orig); });
      }
      orig._retry = true;
      isRefreshing = true;
      try {
        const { refresh } = getTokens();
        if (!refresh) throw new Error('No refresh token');
        const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
        setTokens(res.data);
        processQueue(null, res.data.access_token);
        orig.headers.Authorization = `Bearer ${res.data.access_token}`;
        return client(orig);
      } catch (e) {
        processQueue(e, null);
        clearTokens();
        window.dispatchEvent(new CustomEvent('thehuman:logout'));
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  async signup(data) { const r = await axios.post(`${API}/auth/signup`, data); setTokens(r.data); return r.data; },
  async login(data) { const r = await axios.post(`${API}/auth/login`, data); setTokens(r.data); return r.data; },
  async forgotPassword(email) { return (await axios.post(`${API}/auth/forgot-password`, { email })).data; },
  async resetPassword(token, newPassword) { return (await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword })).data; },
  logout() { clearTokens(); window.dispatchEvent(new CustomEvent('forge:logout')); },
  async getMe() { return (await client.get('/auth/me')).data; },
  isLoggedIn() { return !!getTokens().access; },

  async createBrandKit(data) { return (await client.post('/brand-kits', data)).data; },
  async listBrandKits() { return (await client.get('/brand-kits')).data; },
  async updateBrandKit(id, data) { return (await client.put(`/brand-kits/${id}`, data)).data; },
  async deleteBrandKit(id) { return (await client.delete(`/brand-kits/${id}`)).data; },

  async generate(data) { return (await client.post('/generate', data)).data; },
  async generateAsync(data) { return (await client.post('/generate/async', data)).data; },
  async listGenerations(params = {}) { return (await client.get('/generations', { params })).data; },
  async getGeneration(id) { return (await client.get(`/generations/${id}`)).data; },

  async createBatch(data) { return (await client.post('/batch', data)).data; },
  async listBatches(limit = 10) { return (await client.get('/batch', { params: { limit } })).data; },
  async getBatch(id) { return (await client.get(`/batch/${id}`)).data; },

  async getUsage() { return (await client.get('/usage')).data; },

  async createCheckout(plan) { return (await client.post(`/billing/checkout?plan=${plan}`)).data; },
  async createPortal() { return (await client.post('/billing/portal')).data; },
  async getBilling() { return (await client.get('/billing')).data; },

  async getModels() { return (await axios.get(`${API}/catalog/models`)).data; },
  async getTemplates() { return (await axios.get(`${API}/catalog/templates`)).data; },
  async getPlatforms() { return (await axios.get(`${API}/catalog/platforms`)).data; },

  async uploadFile(file, category = 'brand-assets') {
    const form = new FormData(); form.append('file', file);
    return (await client.post(`/upload?category=${category}`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },

  async joinWaitlist(data) { return (await axios.post(`${API}/waitlist`, data)).data; },
  async getWaitlistCount() { return (await axios.get(`${API}/waitlist/count`)).data; },

  // Profile & Password
  async updateProfile(data) { return (await client.put('/auth/profile', data)).data; },
  async changePassword(data) { return (await client.post('/auth/change-password', data)).data; },

  // Click-to-Ad
  async clickToAd(data) { return (await client.post('/click-to-ad', data)).data; },

  // Teams
  async createTeam(data) { return (await client.post('/teams', data)).data; },
  async listTeams() { return (await client.get('/teams')).data; },
  async getTeam(id) { return (await client.get(`/teams/${id}`)).data; },
  async inviteToTeam(teamId, data) { return (await client.post(`/teams/${teamId}/invite`, data)).data; },
  async removeTeamMember(teamId, email) { return (await client.delete(`/teams/${teamId}/members/${email}`)).data; },
  async shareKitWithTeam(teamId, kitId) { return (await client.post(`/teams/${teamId}/share-kit/${kitId}`)).data; },

  // Search
  async searchGenerations(params = {}) { return (await client.get('/generations/search', { params })).data; },

  // Comparisons
  async createComparison(data) { return (await client.post('/comparisons', data)).data; },
  async listComparisons() { return (await client.get('/comparisons')).data; },
  async updateComparison(id, data) { return (await client.put(`/comparisons/${id}`, { params: data })).data; },
  async deleteComparison(id) { return (await client.delete(`/comparisons/${id}`)).data; },

  // Admin
  async getAdminMetrics(adminKey) { return (await axios.get(`${API}/admin/metrics`, { headers: { 'X-Admin-Key': adminKey } })).data; },
  async getAdminUsers(adminKey, limit = 50) { return (await axios.get(`${API}/admin/users?limit=${limit}`, { headers: { 'X-Admin-Key': adminKey } })).data; },

  // A/B Testing
  async createABTest(data) { return (await client.post('/ab-tests', data)).data; },
  async listABTests(status) { return (await client.get('/ab-tests', { params: status ? { status } : {} })).data; },
  async getABTest(id) { return (await client.get(`/ab-tests/${id}`)).data; },
  async trackABEvent(testId, data) { return (await client.post(`/ab-tests/${testId}/event`, data)).data; },
  async setABWinner(testId, generationId) { return (await client.post(`/ab-tests/${testId}/winner?generation_id=${generationId}`)).data; },
  async pauseABTest(testId) { return (await client.post(`/ab-tests/${testId}/pause`)).data; },
  async deleteABTest(testId) { return (await client.delete(`/ab-tests/${testId}`)).data; },
  async simulateABTraffic(testId, impressions = 100) { return (await client.post(`/ab-tests/${testId}/simulate?impressions=${impressions}`)).data; },
  async getABEmbed(testId) { return (await client.get(`/ab-tests/${testId}/embed`)).data; },
  async getABAttribution(testId) { return (await client.get(`/ab-tests/${testId}/attribution`)).data; },
  async simulateAttribution(testId, events = 500) { return (await client.post(`/ab-tests/${testId}/simulate-attribution?events=${events}`)).data; },

  // Distribution
  async getDistributionPlatforms() { return (await client.get('/distribution/platforms')).data; },
  async createDistribution(data) { return (await client.post('/distributions', data)).data; },
  async listDistributions(limit = 20) { return (await client.get('/distributions', { params: { limit } })).data; },
  async getDistribution(id) { return (await client.get(`/distributions/${id}`)).data; },
};

export function onForceLogout(callback) {
  const handler = () => callback();
  window.addEventListener('thehuman:logout', handler);
  return () => window.removeEventListener('thehuman:logout', handler);
}
